import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, Trash, Star, StarFour, Pencil, PlugsConnected, Plugs, Warning, CheckCircle,
  WhatsappLogo, FacebookLogo, InstagramLogo,
} from "@phosphor-icons/react";

const CHANNEL_LABELS = {
  whatsapp: "WhatsApp",
  messenger: "Messenger",
  instagram: "Instagram",
};

const CHANNEL_ICONS = {
  whatsapp: WhatsappLogo,
  messenger: FacebookLogo,
  instagram: InstagramLogo,
};

const CHANNEL_COLORS = {
  whatsapp: "text-emerald-500",
  messenger: "text-blue-500",
  instagram: "text-pink-500",
};

const EMPTY_ACCOUNT = (channel) => ({
  channel,
  label: "",
  active: true,
  is_default: false,
  phone_number_id: "",
  business_account_id: "",
  display_phone_number: "",
  page_id: "",
  ig_account_id: "",
  access_token: "",
  webhook_verify_token: "",
  app_secret: "",
});

function AccountModal({ channel, initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(initial || EMPTY_ACCOUNT(channel));
  const [saving, setSaving] = useState(false);
  const patch = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.label?.trim()) return toast.error("Nombre requerido");
    setSaving(true);
    try {
      const body = { ...form };
      // Drop masked secrets to preserve existing on backend
      ["access_token", "app_secret"].forEach((k) => {
        if (typeof body[k] === "string" && body[k].startsWith("***")) delete body[k];
      });
      if (isEdit) {
        await api.patch(`/integrations/channel-accounts/${initial.id}`, body);
        toast.success("Cuenta actualizada");
      } else {
        await api.post("/integrations/channel-accounts", body);
        toast.success("Cuenta creada");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid={`channel-account-modal-${channel}`}>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Editar cuenta" : "Agregar cuenta"} · {CHANNEL_LABELS[channel]}
          </DialogTitle>
          <DialogDescription>
            Configura credenciales de esta cuenta. El toggle Activo apaga inbound y outbound sin borrarla.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
          <div className="md:col-span-2">
            <Label>Nombre interno</Label>
            <Input
              value={form.label} onChange={(e) => patch("label", e.target.value)}
              placeholder="Ej. Ventas Kennedy, Servicio Postventa, Motos Yamaha"
              data-testid="channel-account-label"
            />
          </div>
          <div>
            <Label>Access token *</Label>
            <Input
              type="password" value={form.access_token || ""}
              onChange={(e) => patch("access_token", e.target.value)}
              placeholder={isEdit ? "(sin cambios)" : "EAA..."}
              className="font-mono text-xs" data-testid="channel-account-token"
            />
          </div>
          <div>
            <Label>Webhook verify token</Label>
            <Input
              value={form.webhook_verify_token || ""}
              onChange={(e) => patch("webhook_verify_token", e.target.value)}
              className="font-mono text-xs" data-testid="channel-account-verify"
            />
          </div>

          {channel === "whatsapp" && (
            <>
              <div>
                <Label>Phone number ID *</Label>
                <Input
                  value={form.phone_number_id || ""}
                  onChange={(e) => patch("phone_number_id", e.target.value)}
                  data-testid="channel-account-phone-id"
                />
              </div>
              <div>
                <Label>WABA ID</Label>
                <Input
                  value={form.business_account_id || ""}
                  onChange={(e) => patch("business_account_id", e.target.value)}
                  data-testid="channel-account-waba"
                />
              </div>
              <div>
                <Label>Número visible</Label>
                <Input
                  value={form.display_phone_number || ""}
                  onChange={(e) => patch("display_phone_number", e.target.value)}
                  placeholder="+1 809-555-1234"
                  data-testid="channel-account-display"
                />
              </div>
            </>
          )}

          {channel === "messenger" && (
            <div>
              <Label>Page ID *</Label>
              <Input
                value={form.page_id || ""} onChange={(e) => patch("page_id", e.target.value)}
                data-testid="channel-account-page-id"
              />
            </div>
          )}

          {channel === "instagram" && (
            <div>
              <Label>IG Business Account ID *</Label>
              <Input
                value={form.ig_account_id || ""} onChange={(e) => patch("ig_account_id", e.target.value)}
                data-testid="channel-account-ig-id"
              />
            </div>
          )}

          <div>
            <Label>App secret (opcional)</Label>
            <Input
              type="password" value={form.app_secret || ""}
              onChange={(e) => patch("app_secret", e.target.value)}
              placeholder={isEdit ? "(sin cambios)" : ""}
              className="font-mono text-xs"
              data-testid="channel-account-secret"
            />
          </div>

          <div className="flex items-center gap-4 md:col-span-2 pt-2 border-t border-border">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={!!form.active} onCheckedChange={(v) => patch("active", v)}
                data-testid="channel-account-active"
              />
              <span>Activo (recibe y envía mensajes)</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={!!form.is_default} onCheckedChange={(v) => patch("is_default", v)}
                data-testid="channel-account-default"
              />
              <span>Cuenta por defecto para outbound</span>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="channel-account-save">
            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear cuenta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ChannelAccountsSection({ channel, onChange }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const Icon = CHANNEL_ICONS[channel];
  const color = CHANNEL_COLORS[channel];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/integrations/channel-accounts?channel=${channel}`);
      setAccounts(data);
    } catch {
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [channel]);

  const toggle = async (acc) => {
    try {
      await api.post(`/integrations/channel-accounts/${acc.id}/toggle`, { active: !acc.active });
      toast.success(!acc.active ? "Cuenta activada" : "Cuenta desactivada");
      load(); onChange?.();
    } catch { toast.error("Error al cambiar estado"); }
  };

  const setDefault = async (acc) => {
    if (acc.is_default) return;
    try {
      await api.post(`/integrations/channel-accounts/${acc.id}/set-default`);
      toast.success("Marcada como cuenta por defecto");
      load(); onChange?.();
    } catch { toast.error("Error al asignar por defecto"); }
  };

  const remove = async (acc) => {
    if (!window.confirm(`¿Eliminar la cuenta "${acc.label}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/integrations/channel-accounts/${acc.id}`);
      toast.success("Cuenta eliminada");
      load(); onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div
      className="border border-border rounded-lg bg-card mb-4"
      data-testid={`channel-accounts-section-${channel}`}
    >
      <div className="p-5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon size={22} weight="fill" className={color} />
          <div>
            <h3 className="font-heading font-bold text-lg">
              Cuentas conectadas · {CHANNEL_LABELS[channel]}
            </h3>
            <p className="text-xs text-muted-foreground">
              Agrega múltiples números/páginas. Toggle Activo apaga inbound + outbound sin borrar.
            </p>
          </div>
        </div>
        <Button
          onClick={() => { setEditing(null); setModalOpen(true); }}
          data-testid={`channel-account-add-${channel}`}
        >
          <Plus size={14} className="mr-1.5" /> Agregar cuenta
        </Button>
      </div>

      <div className="p-5">
        {loading ? (
          <div className="text-center text-sm text-muted-foreground py-6">Cargando…</div>
        ) : accounts.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-6" data-testid={`channel-accounts-empty-${channel}`}>
            No hay cuentas configuradas. Presiona Agregar cuenta para crear la primera.
          </div>
        ) : (
          <div className="space-y-2">
            {accounts.map((acc) => (
              <div
                key={acc.id}
                data-testid={`channel-account-row-${acc.id}`}
                className={`flex items-center gap-3 p-3 rounded-md border ${
                  acc.active ? "border-border bg-background" : "border-border bg-muted/30 opacity-70"
                }`}
              >
                <div className={`w-9 h-9 rounded-md flex items-center justify-center ${
                  acc.active ? "bg-emerald-500/10" : "bg-muted"
                }`}>
                  <Icon size={16} weight="fill" className={acc.active ? color : "text-muted-foreground"} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-sm truncate">{acc.label}</div>
                    {acc.is_default && (
                      <Badge className="bg-primary text-primary-foreground gap-1 text-[10px]">
                        <Star size={10} weight="fill" /> Por defecto
                      </Badge>
                    )}
                    {acc.active ? (
                      <Badge className="bg-emerald-500 text-white gap-1 text-[10px]">
                        <PlugsConnected size={10} weight="fill" /> Activo
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] gap-1 border-muted-foreground/40 text-muted-foreground">
                        <Plugs size={10} /> Inactivo
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {channel === "whatsapp" && (
                      <>
                        {acc.display_phone_number || acc.phone_number_id || "—"}
                        {acc.business_account_id && ` · WABA ${acc.business_account_id}`}
                      </>
                    )}
                    {channel === "messenger" && (<>Page ID: {acc.page_id || "—"}</>)}
                    {channel === "instagram" && (<>IG ID: {acc.ig_account_id || "—"}</>)}
                    {acc.access_token && ` · token ${acc.access_token}`}
                  </div>
                  {acc.last_error && (
                    <div className="text-[10px] text-rose-500 flex items-center gap-1 mt-0.5">
                      <Warning size={10} weight="fill" /> {acc.last_error.slice(0, 120)}
                    </div>
                  )}
                  {acc.last_verified_at && !acc.last_error && (
                    <div className="text-[10px] text-muted-foreground/70 flex items-center gap-1 mt-0.5">
                      <CheckCircle size={10} weight="fill" className="text-emerald-500"/>
                      Verificado {new Date(acc.last_verified_at).toLocaleString("es-DO")}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <label className="flex items-center gap-1 text-[11px]">
                    <Switch
                      checked={acc.active} onCheckedChange={() => toggle(acc)}
                      data-testid={`channel-account-toggle-${acc.id}`}
                    />
                  </label>
                  <Button
                    variant="ghost" size="sm" onClick={() => setDefault(acc)}
                    disabled={acc.is_default}
                    title={acc.is_default ? "Ya es la cuenta por defecto" : "Marcar como cuenta por defecto"}
                    data-testid={`channel-account-set-default-${acc.id}`}
                  >
                    {acc.is_default ? <Star size={14} weight="fill" className="text-amber-500"/> : <StarFour size={14} />}
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => { setEditing(acc); setModalOpen(true); }}
                    data-testid={`channel-account-edit-${acc.id}`}
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    variant="ghost" size="sm" onClick={() => remove(acc)}
                    className="text-rose-500 hover:text-rose-600"
                    data-testid={`channel-account-delete-${acc.id}`}
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <AccountModal
          channel={channel}
          initial={editing}
          onClose={() => setModalOpen(false)}
          onSaved={() => { load(); onChange?.(); }}
        />
      )}
    </div>
  );
}
